import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Search,
} from '@makeably/creativex-design-system';
import {
  emptyState,
  getBoolDimension,
} from 'components/internal/shared';
import ItemsTable from 'components/molecules/ItemsTable';
import { getItemSortBy } from 'utilities/item';
import { searchItems } from 'utilities/itemSearch';
import {
  splitSnakeCase,
  titleize,
} from 'utilities/string';

const propTypes = {
  company: PropTypes.string.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    lastName: PropTypes.string.isRequired,
    profile: PropTypes.string.isRequired,
  }).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const headers = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'enabled',
    label: 'Enabled',
  },
  {
    key: 'id',
    label: 'Role Key',
  },
];

function getItems(roles, userRolesSet) {
  return roles.map((role) => ({
    enabled: getBoolDimension(userRolesSet.has(role), true),
    id: { value: role },
    name: { value: titleize(splitSnakeCase(role)) },
  }));
}

function UserRoles({
  company,
  roles,
  user,
  userRoles,
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({
    asc: true,
    key: 'name',
  });

  const items = useMemo(() => {
    const userRolesSet = new Set(userRoles);
    return getItems(roles, userRolesSet);
  }, [roles, userRoles]);

  const searchedItems = useMemo(() => (
    searchItems(items, search, ['id', 'name'])
  ), [items, search]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return searchedItems.sort(byKeyDir).slice();
  }, [searchedItems, sort]);

  return (
    <Card className="u-flexColumn u-gap-24">
      <div className="u-flexColumn u-gap-8">
        <h4>{ `Roles for ${user.firstName} ${user.lastName} (${user.id})` }</h4>
        <h5>{ `${company}: ${titleize(user.profile)}` }</h5>
      </div>
      <Search
        placeholder="Search by Name, Role Key"
        value={search}
        onChange={setSearch}
      />
      <ItemsTable
        className="u-scrollShadowRight"
        emptyTableContent={emptyState}
        headers={headers}
        items={sortedItems}
        sort={sort}
        onSortChange={setSort}
      />
    </Card>
  );
}

UserRoles.propTypes = propTypes;

export default UserRoles;
